import { lazy, Suspense } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
// layouts
import DashboardLayout from './layouts/dashboard';
import SuperAdminLayout from './layouts/super-admin';
import SimpleLayout from './layouts/simple';
// components
import RoleGuard from './components/auth/RoleGuard';
import RouteFallback from './components/route-fallback';
import { getAuthContext, getDefaultRoute } from './utils/roleAccess';

// ----------------------------------------------------------------------

const Page404 = lazy(() => import('./pages/Page404'));
const LoginComponent = lazy(() => import('./components/login-component/login'));
const SignupComponent = lazy(() => import('./components/login-component/signup'));
const ForgotPasswordComponent = lazy(() => import('./components/login-component/forgotPassword'));

const AdminDashboard = lazy(() => import('./components/dashboard-component/admin-dashboard'));
const AddClientComponent = lazy(() => import('./components/client-component/addClient'));
const ClientsComponent = lazy(() => import('./components/client-component/clients'));
const AddPackageComponent = lazy(() => import('./components/package-component/addPackage'));
const PackagesComponent = lazy(() => import('./components/package-component/packageList'));
const PrintInvoiceComponent = lazy(() => import('./components/package-component/printInvoice'));
const AddEmployeeComponent = lazy(() => import('./components/employee-component/addEmployee'));
const EmployeesComponent = lazy(() => import('./components/employee-component/employees'));
const AttendanceManagement = lazy(() => import('./components/employee-component/attendanceManagement'));
const GenerateBillComponent = lazy(() => import('./components/billing-component/generateBill'));
const ShowBillsComponent = lazy(() => import('./components/billing-component/showBills'));
const ScanPackage = lazy(() => import('./components/package-update-component/scanPackage'));
const HubQueue = lazy(() => import('./components/package-update-component/hubQueue'));
const ScannedPackages = lazy(() => import('./components/package-update-component/scannedPackage'));
const ScanOutOfDelivery = lazy(() => import('./components/package-update-component/scanOutOfDelivery'));
const OutOfDeliveryPackages = lazy(() => import('./components/package-update-component/outOfDeliveryPackages'));
const DeliveredScanner = lazy(() => import('./components/package-update-component/delivered'));
const DeliveryLists = lazy(() => import('./components/package-update-component/deliveryLists'));
const Deliveries = lazy(() => import('./components/package-update-component/deliveries'));
const ToPays = lazy(() => import('./components/package-update-component/toPays'));
const Collections = lazy(() => import('./components/package-update-component/collections'));
const AddVehicleComponent = lazy(() => import('./components/vehicle-component/addVehicle'));
const VehiclesComponent = lazy(() => import('./components/vehicle-component/vehicles'));
const EnquiriesComponent = lazy(() => import('./components/enquiry-component/enquiries'));
const WhatsappInboxComponent = lazy(() => import('./components/whatsapp-inbox/whatsappInbox'));
const UpdateProfile = lazy(() => import('./components/update-profile-component/updateProfile'));
const MobileHome = lazy(() => import('./components/mobile-home/MobileHome'));

const SuperAdminDashboard = lazy(() => import('./components/super-admin/SuperAdminDashboard'));
const SuperAdminAdmins = lazy(() => import('./components/super-admin/SuperAdminAdmins'));
const SuperAdminAdminDetail = lazy(() => import('./components/super-admin/SuperAdminAdminDetail'));
const SuperAdminPackages = lazy(() => import('./components/super-admin/SuperAdminPackages'));
const SuperAdminPayments = lazy(() => import('./components/super-admin/SuperAdminPayments'));
const SuperAdminUsers = lazy(() => import('./components/super-admin/SuperAdminUsers'));
const SuperAdminCourierPartners = lazy(() => import('./components/super-admin/SuperAdminCourierPartners'));
const TransferToThirdParty = lazy(() => import('./components/package-update-component/transferToThirdParty'));
const RtoActions = lazy(() => import('./components/package-update-component/rtoActions'));
const RtoList = lazy(() => import('./components/package-update-component/rtoList'));

// ----------------------------------------------------------------------

const guarded = (path, Component) => ({
  path,
  element: (
    <RoleGuard path={path}>
      <Component />
    </RoleGuard>
  ),
});

export default function Router() {
  const isLoggedIn = !!localStorage.getItem('user');
  const { isSuperAdmin } = getAuthContext();

  const routes = useRoutes(
    [
      isLoggedIn &&
        isSuperAdmin && {
          path: 'super-admin',
          element: <SuperAdminLayout />,
          children: [
            {
              index: true,
              element: (
                <RoleGuard path="super-admin">
                  <SuperAdminDashboard />
                </RoleGuard>
              ),
            },
            {
              path: 'admins',
              element: (
                <RoleGuard path="super-admin/admins">
                  <SuperAdminAdmins />
                </RoleGuard>
              ),
            },
            {
              path: 'admins/:id',
              element: (
                <RoleGuard path="super-admin/admins">
                  <SuperAdminAdminDetail />
                </RoleGuard>
              ),
            },
            {
              path: 'packages',
              element: (
                <RoleGuard path="super-admin/packages">
                  <SuperAdminPackages />
                </RoleGuard>
              ),
            },
            {
              path: 'payments',
              element: (
                <RoleGuard path="super-admin/payments">
                  <SuperAdminPayments />
                </RoleGuard>
              ),
            },
            {
              path: 'users',
              element: (
                <RoleGuard path="super-admin/users">
                  <SuperAdminUsers />
                </RoleGuard>
              ),
            },
            {
              path: 'courier-partners',
              element: (
                <RoleGuard path="super-admin/courier-partners">
                  <SuperAdminCourierPartners />
                </RoleGuard>
              ),
            },
          ],
        },
      isLoggedIn &&
        !isSuperAdmin && {
          path: '',
          element: <DashboardLayout />,
          children: [
            { index: true, element: <Navigate to={getDefaultRoute()} replace /> },
            guarded('home', MobileHome),
            guarded('dashboard-admin', AdminDashboard),
            guarded('add-client', AddClientComponent),
            guarded('clients', ClientsComponent),
            guarded('add-package', AddPackageComponent),
            guarded('add-employee', AddEmployeeComponent),
            guarded('employees', EmployeesComponent),
            guarded('attendance', AttendanceManagement),
            guarded('packages', PackagesComponent),
            guarded('print-invoice', PrintInvoiceComponent),
            guarded('generate-billing', GenerateBillComponent),
            guarded('show-bills', ShowBillsComponent),
            guarded('scan-package', ScanPackage),
            guarded('hub-queue', HubQueue),
            guarded('transfer-third-party', TransferToThirdParty),
            guarded('rto', RtoActions),
            guarded('rto-list', RtoList),
            guarded('scan-out-of-delivery', ScanOutOfDelivery),
            guarded('delivered', DeliveredScanner),
            guarded('delivery-lists', DeliveryLists),
            guarded('deliveries', Deliveries),
            guarded('to-pays', ToPays),
            guarded('collections', Collections),
            guarded('out-of-delivery-packages', OutOfDeliveryPackages),
            guarded('scanned-packages', ScannedPackages),
            guarded('add-vehicle', AddVehicleComponent),
            guarded('vehicles', VehiclesComponent),
            guarded('enquiries', EnquiriesComponent),
            guarded('whatsapp-inbox', WhatsappInboxComponent),
            guarded('update-profile', UpdateProfile),
          ],
        },
      { path: 'login', element: <LoginComponent /> },
      { path: 'signup', element: <SignupComponent /> },
      { path: 'reset-password', element: <ForgotPasswordComponent /> },
      {
        element: <SimpleLayout />,
        children: [{ path: '404', element: <Page404 /> }],
      },
      {
        path: '*',
        element: <Navigate to={isLoggedIn ? getDefaultRoute() : '/login'} replace />,
      },
    ].filter(Boolean)
  );

  return <Suspense fallback={<RouteFallback />}>{routes}</Suspense>;
}
